// Config is driven entirely by EXPO_PUBLIC_* environment variables,
// which are written to .env.local by `npm run env:development` (or :production)
// via `serverless export-env --stage <stage>` pulling from SSM.
//
// Mirrors the pattern in frontend/client/serverless.yml.
// To build env: npm run env:development  (requires AWS credentials + SSM access)

const ENV = process.env.EXPO_PUBLIC_ENV || 'development';
const REGION = process.env.EXPO_PUBLIC_REGION || 'us-east-1';
const GOOGLE_MAPS_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY || '';

// Fallback API endpoints when env vars aren't set (local dev without SSM)
const fallbackEndpoints: Record<string, string> = {
  development: 'https://development-api.eleplan.org/client',
  production: 'https://production-api.eleplan.com/client',
};

// Web app base URL (for "Manage billing" / open in browser)
const webAppBaseUrls: Record<string, string> = {
  development: 'https://development.eleplan.org',
  production: 'https://app.eleplan.com',
};

// Bare marketing domain — mirrors web's REACT_APP_DOMAIN (frontend/client/serverless.yml).
// Surveys expose this as the {app_domain} placeholder so question text can reference it.
const appDomains: Record<string, string> = {
  development: 'eleplan.org',
  production: 'eleplan.com',
};

const clientApiEndpoint =
  process.env.EXPO_PUBLIC_API_ENDPOINT || fallbackEndpoints[ENV] || fallbackEndpoints.development;

// Derive sibling-service endpoints from the client endpoint. All services
// share the same {stage}-api custom domain — only the basePath differs.
// Each can be overridden via SSM (EXPO_PUBLIC_<SERVICE>_API_ENDPOINT) so a
// canary can point one service at a different host without affecting the
// others. Future service splits follow the same pattern.
const billingApiEndpoint =
  process.env.EXPO_PUBLIC_BILLING_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/billing');

const projectsApiEndpoint =
  process.env.EXPO_PUBLIC_PROJECTS_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/projects');

const partnersApiEndpoint =
  process.env.EXPO_PUBLIC_PARTNERS_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/partners');

const adminApiEndpoint =
  process.env.EXPO_PUBLIC_ADMIN_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/admin');

const cadenceApiEndpoint =
  process.env.EXPO_PUBLIC_CADENCE_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/cadence');

const provenantApiEndpoint =
  process.env.EXPO_PUBLIC_PROVENANT_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/provenant');

// Our own short-link domain. Proof receipts are addressed here by commit id
// (https://<host>/p/<id>) rather than at the raw API hostname, so anything a
// reader is asked to open sits on a domain that means something to them.
const linkHost =
  process.env.EXPO_PUBLIC_LINK_HOST || (ENV === 'production' ? 'eleplan.link' : 'development.eleplan.link');

const jobsApiEndpoint =
  process.env.EXPO_PUBLIC_JOBS_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/jobs');

const wellnessApiEndpoint =
  process.env.EXPO_PUBLIC_WELLNESS_API_ENDPOINT || clientApiEndpoint.replace(/\/client(\/?)?$/, '/wellness');

const config = {
  env: ENV,
  apiGateway: {
    endpoint: clientApiEndpoint,
    region: REGION,
    name: 'client',
  },
  billingApi: {
    endpoint: billingApiEndpoint,
    region: REGION,
    name: 'billing',
  },
  projectsApi: {
    endpoint: projectsApiEndpoint,
    region: REGION,
    name: 'projects',
  },
  partnersApi: {
    endpoint: partnersApiEndpoint,
    region: REGION,
    name: 'partners',
  },
  adminApi: {
    endpoint: adminApiEndpoint,
    region: REGION,
    name: 'admin',
  },
  cadenceApi: {
    endpoint: cadenceApiEndpoint,
    region: REGION,
    name: 'cadence',
  },
  provenantApi: {
    endpoint: provenantApiEndpoint,
    region: REGION,
    name: 'provenant',
  },
  linkHost,
  jobsApi: {
    endpoint: jobsApiEndpoint,
    region: REGION,
    name: 'jobs',
  },
  wellnessApi: {
    endpoint: wellnessApiEndpoint,
    region: REGION,
    name: 'wellness',
  },
  cognito: {
    region: REGION,
    userPoolId: process.env.EXPO_PUBLIC_USER_POOL_ID || '',
    appClientId: process.env.EXPO_PUBLIC_APP_CLIENT_ID || '',
    identityPoolId: process.env.EXPO_PUBLIC_IDENTITY_POOL_ID || '',
    // Hosted UI domain — mirrors web's eleplan-{stage}.auth.{region}.amazoncognito.com pattern.
    // Override via EXPO_PUBLIC_COGNITO_DOMAIN if the domain differs from this convention.
    domain: process.env.EXPO_PUBLIC_COGNITO_DOMAIN || `eleplan-${ENV}.auth.${REGION}.amazoncognito.com`,
    // Custom scheme redirect registered in the Cognito App Client's allowed callback URLs.
    mobileCallbackScheme: 'com.eleplan.app://cognito-callback',
    // HTTPS callback URL used on Platform.OS === 'web' for Cognito
    // Hosted UI sign-in. The browser can't open `com.eleplan.app://`
    // schemes, so the popup needs an HTTPS destination — this URL
    // points at the `auth/cognito-callback` Lambda which renders a
    // page that postMessages the auth code back to the parent
    // `window.opener`. The Lambda lives in
    // `backend/api/client/controllers/auth/cognito-callback/`; the
    // URL is registered as a Cognito CallbackURL in
    // `backend/api/client/resources/cognito-user-pool.yml`. Override
    // via EXPO_PUBLIC_COGNITO_WEB_CALLBACK_URL when a non-default
    // backend URL is in use.
    //
    // APPENDED to the client endpoint, not swapped for its basePath. The
    // sibling-service derivations above replace `/client` with `/billing`,
    // `/projects` etc. because those ARE sibling basePaths on the same custom
    // domain. `auth/cognito-callback` is not — it is a route INSIDE the client
    // service (backend/api/client/controllers/auth/cognito-callback), so it
    // lives under `/client`.
    //
    // Copying the sibling idiom here produced
    // `production-api.eleplan.com/auth/cognito-callback`, a path with no API
    // mapping on that domain — API Gateway answers `{"message":"Forbidden"}`
    // and web Google/Apple sign-in dies on a blank popup showing that JSON.
    webCallbackUrl: process.env.EXPO_PUBLIC_COGNITO_WEB_CALLBACK_URL
      || `${clientApiEndpoint.replace(/\/+$/, '')}/auth/cognito-callback`,
  },
  streaming: {
    // Project-level KB chat (replaces window.CustomEvent kb-stream pattern)
    chatUrl: process.env.EXPO_PUBLIC_STREAM_URL || '',
    // Account-level AI assistant
    accountChatUrl: process.env.EXPO_PUBLIC_ACCOUNT_STREAM_URL || '',
    // Project creation chat
    createChatUrl: process.env.EXPO_PUBLIC_CREATE_CHAT_URL || '',
    // Admin Ops Assistant Function URL — backs the /Admin → Ops Assistant
    // tab. Posts {account_id, cognito_id, query, conversation_id?,
    // query_source?} as JSON; streams NDJSON deltas + status events.
    adminOpsUrl: process.env.EXPO_PUBLIC_ADMIN_OPS_URL || '',
    // Admin "Query a Project" Function URL — backs /Admin → Query Project. Runs a
    // real Ellie query against any project 1:1 (reuses the live KB pipeline as a
    // project member). Posts {account_id, cognito_id, project_id, prompt}; streams
    // NDJSON deltas + status events.
    adminQueryUrl: process.env.EXPO_PUBLIC_ADMIN_QUERY_URL || '',
    // Analytics chat Function URL — backs the "Ask the data" panel on admin
    // analytics screens (adminAnalyticsChatStream lambda). Posts {account_id,
    // cognito_id, domain, query, conversation_id?}; streams NDJSON deltas +
    // status events.
    analyticsChatUrl: process.env.EXPO_PUBLIC_ANALYTICS_CHAT_URL || '',
    // Realtime voice supervisor Function URL. Backs the
    // `consult_supervisor` tool call the realtime model makes — the
    // supervisor lambda streams NDJSON `{type:'delta', data}` frames
    // back as the cheaper text model generates them, and the realtime
    // client pipes each completed sentence into the realtime
    // conversation so the user starts hearing the answer in under a
    // second instead of after the full 5-15s orchestration loop.
    realtimeSupervisorUrl: process.env.EXPO_PUBLIC_REALTIME_SUPERVISOR_URL || '',
  },
  // Realtime voice: routes through apiClient (regular API Gateway HTTP API
  // + JWT authorizer), so no per-endpoint URLs need to live in config.
  // The mobile-facing endpoints are:
  //   POST /realtime/mint-session/{account_id}/{cognito_id}
  //   POST /realtime/end-session/{account_id}/{cognito_id}
  //   GET  /realtime/quota/{account_id}/{cognito_id}
  // Tool calls are NOT a mobile endpoint — OpenAI's Realtime API calls our
  // MCP Function URL server-to-server during voice sessions.
  openai: {
    // Lambda function URL for OpenAI TTS — same endpoint used by the web app
    ttsUrl: process.env.EXPO_PUBLIC_TTS_ENDPOINT || 'https://hum7ztkaolmu5h5yrfkoxcxbxa0zobud.lambda-url.us-east-1.on.aws',
  },
  webApp: {
    baseUrl: process.env.EXPO_PUBLIC_WEB_APP_URL || webAppBaseUrls[ENV] || webAppBaseUrls.development,
    // Bare marketing domain (e.g. "eleplan.org" / "eleplan.com"), used as the
    // {app_domain} SurveyJS placeholder. Override via EXPO_PUBLIC_APP_DOMAIN.
    appDomain: process.env.EXPO_PUBLIC_APP_DOMAIN || appDomains[ENV] || appDomains.development,
  },
  // Inbound mail domain — project inboxes are {mnemonic}@m.{mailDomain}
  // user personal inbox is {user.username}@m.{mailDomain}
  mailDomain: ENV === 'production' ? 'm.eleplan.com' : 'm.eleplan.org',
  slack: {
    // Feedback webhook (account deletion request) — same as web REACT_APP_SLACK_FEEDBACK_URL
    feedbackUrl: process.env.EXPO_PUBLIC_SLACK_FEEDBACK_URL || '',
    // Error boundary crash webhook — same channel as web REACT_APP_SLACK_ERROR_HOOK_URL
    errorHookUrl: process.env.EXPO_PUBLIC_SLACK_ERROR_HOOK_URL || '',
  },
  google: {
    // Calendar OAuth — Option A: Web client only, HTTPS callback, claim flow
    clientIdWeb: process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID_WEB || '',
    // Where Google redirects after consent; must match backend and Google Console exactly (use SSM calendar-oauth-callback-url).
    oauthCallbackUrl: process.env.EXPO_PUBLIC_GOOGLE_OAUTH_CALLBACK_URL
      || (process.env.EXPO_PUBLIC_API_ENDPOINT || fallbackEndpoints[ENV] || fallbackEndpoints.development) + '/calendar/oauth-callback',
    appRedirectScheme: process.env.EXPO_PUBLIC_GOOGLE_APP_REDIRECT_SCHEME || 'com.eleplan.app://google-auth',
    // Gmail scanning OAuth — same Google client, different scopes + redirect
    gmailOauthCallbackUrl: process.env.EXPO_PUBLIC_GMAIL_OAUTH_CALLBACK_URL
      || (process.env.EXPO_PUBLIC_API_ENDPOINT || fallbackEndpoints[ENV] || fallbackEndpoints.development) + '/gmail/oauth-callback',
    gmailAppRedirectScheme: process.env.EXPO_PUBLIC_GMAIL_APP_REDIRECT_SCHEME || 'com.eleplan.app://gmail-auth',
    // Places API key for address autocomplete in member/contact forms
    // Restrict this key to the Places API in the Google Cloud Console.
    placesApiKey: process.env.EXPO_PUBLIC_GOOGLE_PLACES_API_KEY || '',
    // Native Android Maps key (do not fall back to Places key).
    mapsApiKey: GOOGLE_MAPS_API_KEY,
    // Android map rendering guard for react-native-maps Google provider.
    // Enabled only when explicit flag is on, or when a dedicated Maps key is present.
    androidMapsEnabled:
      process.env.EXPO_PUBLIC_ANDROID_MAPS_ENABLED === 'true'
      || !!GOOGLE_MAPS_API_KEY,
    // Google Picker (web/desktop "Import from Google Drive" upload flow).
    // Mirrors the eleplan web app's REACT_APP_GOOGLE_PICKER_API_KEY /
    // REACT_APP_GOOGLE_PICKER_CLIENT_ID — both are scoped/restricted in the
    // Google Cloud Console to the eleplan origins (localhost:3000, the
    // staging/prod web URLs). Mobile RN-Web/Electron dev runs on the same
    // localhost:3000 (`expo start --web --port 3000`), so the existing dev
    // credentials are reused as the fallback. Production builds set the
    // SSM-sourced env vars during `npm run env:production`.
    pickerApiKey:
      process.env.EXPO_PUBLIC_GOOGLE_PICKER_API_KEY
      || 'AIzaSyCYlA_1ujos2dqYbHuoLKKsdfQE7SCY3Sw',
    pickerClientId:
      process.env.EXPO_PUBLIC_GOOGLE_PICKER_CLIENT_ID
      || '570835884148-nbf6aqd3dj1u6oihdsprn66qve82t80h.apps.googleusercontent.com',
  },
  dropbox: {
    // Dropbox Chooser (web/desktop "Import from Dropbox" upload flow).
    // Mirrors the eleplan web app's hard-coded `hvsnblv1pd9vs7l` app key
    // (Dropbox apps allow-list domains, not API keys, so the same key works
    // anywhere the origin is registered). Override via SSM for prod if a
    // separate mobile-web Dropbox app is provisioned.
    appKey: process.env.EXPO_PUBLIC_DROPBOX_APP_KEY || 'hvsnblv1pd9vs7l',
  },
  watch: {
    // Build-time kill switch. Runtime settings/account flags must also enable watch.
    enabled: true
  },
  logo: {
    // logo.dev publishable key — used to fetch company logos in the Vault by domain.
    // Get a key at https://www.logo.dev and set EXPO_PUBLIC_LOGO_DEV_TOKEN in SSM / .env.local
    devToken: process.env.EXPO_PUBLIC_LOGO_DEV_TOKEN || 'pk_Lekiag4fQ-S6Rwnu5-_Mpw',
  },
  stripe: {
    // Stripe publishable key — only used by the web build's BillingScreen → AddCardModal
    // (Stripe Elements). Native iOS uses StoreKit/IAP and never sees this; native Android
    // hands off to the web app for purchase. Mirrors web's REACT_APP_STRIPE_PUBLIC_KEY,
    // sourced from SSM `stripe-public-key` via `npm run env:*`. Fallback is the same
    // legacy test key used in frontend/client/src/config.js.
    publishableKey:
      process.env.EXPO_PUBLIC_STRIPE_PUBLIC_KEY
      || 'pk_test_51OHDsrAMB6p3TXwRokoqoGcx8mPvWHCZAIMlbSbWBHZI7ez5lnFMdEXfnpxiGoONnjr8mqyKt9aE4W6tcdL3dA2a00rtrNchSH',
  },
  webPush: {
    // VAPID public key for browser Push API subscriptions. Web/desktop only —
    // native iOS/Android use APNs/FCM via expo-notifications. Sourced from
    // SSM `public-vapid-key` (same param as legacy web's REACT_APP_PUBLIC_VAPID_KEY).
    publicKey: process.env.EXPO_PUBLIC_VAPID_PUBLIC_KEY || '',
  },
};

export default config;
